"use client";
import React from "react";
import {
  Grid,
  GridContent,
  GridItem,
  GridLoadMore,
} from "@aleksundshantu/aunds-utils-react/primitives/grid";
import { GlossarGridOptionsType } from "@/types/ModuleOptionsType";
import {
  GlossarCard,
  GlossarCardElement,
} from "@/components/Glossar/GlossarCard/GlossarCard";
import { GlossarFilter } from "@/components/Glossar/GlossarFilter/GlossarFilter";
import { useAnimeKeyWithDep } from "@/hooks/useAnimeKey";
import { GlossarSearch } from "@/components/Glossar/GlossarSearch/GlossarSearch";
import { Options_Header_Glossar_Master } from "@aleksundshantu/gp-types/acf-header-options";
import buttonStyles from "../../components/Button/Button.module.scss";
import { fetchGlossar } from "@/actions/fetchGlossar";
import { mockGlossar } from "@/ui/mock-data";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import { GlossarGridModuleType } from "./GlossarGrid.server";

interface GlossarGridProps {
  data: GlossarGridModuleType;
  glossarGridOptions: {
    box: GlossarGridOptionsType["box"];
    grid_options: {
      grid_navigation: GlossarGridOptionsType["grid_options"]["grid_navigation"];
    };
  };
  glossarHeaderOptions: {
    show_tooltip: Options_Header_Glossar_Master["show_tooltip"];
  };
  initialPosts: GlossarCardElement[];
  initialTotalPosts: number;
  filterChars: Record<string, string>;
}

const GlossarGridClient = ({
  data,
  glossarGridOptions,
  glossarHeaderOptions,
  initialPosts,
  initialTotalPosts,
  filterChars,
}: GlossarGridProps) => {
  const { background_color_class, posts_per_page } = data.layout;

  const {
    box: { show_border_radius, show_box_shadow, show_card_box },
    grid_options: { grid_navigation },
  } = glossarGridOptions ?? {};

  const { locale } = useGlobalContext();

  const [activeLetter, setActiveLetter] = React.useState("all");

  const refreshKey = useAnimeKeyWithDep({
    ext: "refresh",
    deps: [activeLetter],
  });

  const { categories, type } = data.glossary_grid;

  {
    /* jscpd:ignore-start */
  }

  return (
    <Grid
      postsPerPage={posts_per_page}
      refreshKey={refreshKey}
      initialItems={initialPosts}
      initialTotalPosts={initialTotalPosts}
      fetchItems={({ page, postsPerPage, offset }) => {
        return fetchGlossar({
          page,
          postsPerPage,
          offset,
          locale,
          ...(type === "categories" && categories
            ? {
                type: "categories",
                categories,
              }
            : { type: "posts" }),
          activeLetter,
          showTooltip: glossarHeaderOptions.show_tooltip,
        });
      }}
      renderLoading
      initialExtraData={{ filterChars }}
    >
      {(gridPosts, loading) => {
        return (
          <>
            <GlossarSearch setActiveLetter={setActiveLetter} />
            <GlossarFilter
              activeLetter={activeLetter}
              setActiveLetter={setActiveLetter}
              extraClasses={background_color_class || ""}
            />
            <GridContent justifyContent="center" gap="2rem">
              {gridPosts.map((element, key) => {
                return (
                  <GridItem
                    itemWidth={{
                      mobile: "100%",
                      tablet: "50%",
                      desktop: "33.333%",
                    }}
                    key={key}
                  >
                    <GlossarCard
                      skeleton={loading}
                      element={
                        loading ? mockGlossar : (element as GlossarCardElement)
                      }
                      show_border_radius={show_border_radius}
                      show_box_shadow={show_box_shadow}
                      showBox={show_card_box}
                    />
                  </GridItem>
                );
              })}
            </GridContent>
            <GridLoadMore
              grid_navigation={grid_navigation}
              buttonStyles={buttonStyles}
              iconPosition="left"
            />
          </>
        );
      }}
    </Grid>
  );

  {
    /* jscpd:ignore-end */
  }
};

export default GlossarGridClient;
